import { ApiError, authHeaders, requestJson } from './http';

import { OFFLINE_SESSION_MAX_AGE_MS, hasCurrentOfflineLease, removeOfflineFamily } from './offlineSession';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
const CACHE_EPOCH_KEY_PREFIX = 'luma-household-cache-epochs-v1:';
const CACHE_LEASE_KEY_PREFIX = 'luma-household-cache-epochs-lease-v1:';

interface CacheEpoch {
  household_id?: string;
  household_opaque_id: string;
  cache_epoch: number;
}


export function getKnownCacheEpoch(householdId: string): number | null {
  const userId = localStorage.getItem('user_id');
  if (!userId) return null;
  try {
    // Renewed only by the uncached authorization endpoint, never cached audio.
    const checkedAt = Number(localStorage.getItem(CACHE_LEASE_KEY_PREFIX + userId));
    const age = Date.now() - checkedAt;
    if (!checkedAt || !Number.isFinite(age) || age < 0 || (age >= OFFLINE_SESSION_MAX_AGE_MS && !hasCurrentOfflineLease(userId, householdId))) return null;
    const persisted = JSON.parse(localStorage.getItem(CACHE_EPOCH_KEY_PREFIX + userId) || '{}') as Record<string, unknown>;
    const value = persisted[householdId];
    return typeof value === 'number' ? value : null;
  } catch {
    return null;
  }
}

export async function reconcileOfflineCacheEpochs(): Promise<void> {
  const token = localStorage.getItem('auth_token');
  const userId = localStorage.getItem('user_id');
  if (!token || !userId) return;
  const cacheEpochKey = CACHE_EPOCH_KEY_PREFIX + userId;
  try {
    const current = await requestJson<CacheEpoch[]>(`${API_BASE_URL}/operations/cache-epochs`, {
      headers: authHeaders(),
    });
    if (userId !== localStorage.getItem('user_id') || token !== localStorage.getItem('auth_token')) return;
    const persistedEpochs: Record<string, number> = {};
    current.forEach((item) => {
      // Keep both identifiers locally: API cache entries are scoped by the
      // household UUID while the revocation comparison uses the opaque ID.
      if (item.household_id) {
        persistedEpochs[item.household_id] = item.cache_epoch;
      }
      persistedEpochs[item.household_opaque_id] = item.cache_epoch;
    });
    const previous = JSON.parse(localStorage.getItem(cacheEpochKey) || '{}') as Record<string, number>;
    const changed = Object.keys(previous).some((key) => persistedEpochs[key] !== previous[key]);
    localStorage.setItem(
      cacheEpochKey,
      JSON.stringify(persistedEpochs)
    );
    localStorage.setItem(CACHE_LEASE_KEY_PREFIX + userId, String(Date.now()));
    if (changed) {
      window.dispatchEvent(new Event('luma-access-invalidated'));
      removeOfflineFamily(userId);
      localStorage.removeItem('luma:approved-cache:v1:' + userId);
      navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_HOUSEHOLD_CACHES' });
    }
  } catch (error) {
    if (error instanceof ApiError && error.code === 'unauthorized') throw error;
    // Offline is expected at bedtime. The next authenticated online load retries.
  }
}
